import type { ReactNode } from "react";

import { PageHeader, type PageHeaderProps } from "./PageHeader";

// Phase 4e brand-visual-rollout (site-wide polish) — full-bleed
// flag-navy hero band used by every customer page that benefits
// from a banner: catalog browse, search results, cart, auth pages,
// portal pages, etc.
//
// The "FULL_BLEED" trick (used in HomePage too): the parent
// CustomerApp's <main> has `max-w-6xl` + padding; this section
// breaks out with `relative left-1/2 -mx-[50vw] w-screen`, then
// re-constrains its inner content to max-w-6xl. The CustomerApp
// container has overflow-x-clip so this doesn't produce a
// horizontal scrollbar.
//
// One-liner usage:
//   <PageHero eyebrow="Browse" title="Skid Steers" description="..." />
//
// With photo:
//   <PageHero eyebrow="Browse" title="Skid Steers" imageUrl={img} />
//
// With CTA:
//   <PageHero ... actions={<><Button/>...</>} />

export interface PageHeroProps extends Omit<PageHeaderProps, "tone"> {
  // Optional background image — composites under a flag-navy gradient
  // overlay so heading text stays legible.
  imageUrl?: string | undefined;
  // Optional right-rail content (e.g., a service-area card). When
  // present, the layout switches to a 2-col grid on lg.
  rail?: ReactNode;
  size?: "sm" | "md" | "lg";
}

export function PageHero({
  imageUrl,
  rail,
  size = "md",
  ...headerProps
}: PageHeroProps): JSX.Element {
  const padding =
    size === "sm"
      ? "py-10 md:py-14"
      : size === "lg"
      ? "py-16 md:py-24 lg:py-28"
      : "py-12 md:py-16 lg:py-20";

  return (
    <section
      className={`relative left-1/2 right-1/2 -mx-[50vw] w-screen isolate overflow-hidden ${
        imageUrl ? "bg-flag-navy-dark" : "bg-hero-flag"
      } text-brand-white`}
    >
      {/* Optional photo behind the gradient. */}
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
      ) : null}
      {/* Radial brand-red glow + bottom-to-top dark gradient so heading
          text stays legible over the photo. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 ${
          imageUrl
            ? "bg-gradient-to-t from-flag-navy-dark via-flag-navy/70 to-flag-navy/40"
            : "bg-hero-glow"
        }`}
      />
      {/* Top brand-red hairline — mirrors NavHeader / Footer accents. */}
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-brand-red" />

      <div className={`relative mx-auto max-w-6xl px-4 md:px-6 ${padding}`}>
        {rail ? (
          <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
            <PageHeader {...headerProps} tone="hero" />
            <div>{rail}</div>
          </div>
        ) : (
          <PageHeader {...headerProps} tone="hero" />
        )}
      </div>
    </section>
  );
}
